<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
    UiFormField,
    UiText,
    generateInitials,
    type ComboboxItemData,
    UiAsyncCombobox,
    UserSearchResultSchema,
} from '@shopexperts/shared';
import { useUserSearch } from '../../composables/reviews/useUserSearch';
import { z } from 'zod';

const props = defineProps<{
    name?: string;
    label?: string;
    disabled?: boolean;
}>();

const clientObject = defineModel<z.infer<typeof UserSearchResultSchema>| undefined>('clientObject');
const clientId = defineModel<string | undefined>('clientId');
const clientEmail = defineModel<string | undefined | null>('email');
const selectedItems = ref<ComboboxItemData[]>([]);

const email = computed(() => {
    if (clientId.value) return clientObject.value?.email ?? '';
    if (searchQuery.value.length >= 2 && !clientId.value) {
        return searchQuery.value;
    }
    return '';
});

const searchQuery = ref<string>('');
const isExistingClient = computed(() => !!clientId.value);

const {
    isSearching,
    data,
    hasResults,
    searchError,
} = useUserSearch(searchQuery);

const searchResults = computed<ComboboxItemData[]>(() => {
    const items = data.value?.map((user) => ({
        value: user.client?.id?.toString() || '',
        label: user.user_name || '',
        image: user.profile_picture || undefined,
        rounded: true,
        description: user.email || '',
        initials: generateInitials(user.user_name || ''),
        showImage: true,
        metadata: {
            email: user.email,
            clientRecord: user,
        },
    }));

    return items || [];
});

watch([clientId, selectedItems, data], ([id, selected, results]) => {
    if (!id) { clientObject.value = undefined; return; }
    const record = UserSearchResultSchema.safeParse(
        selected.find(item => item.value === id)?.metadata?.clientRecord,
    );
    clientObject.value = record.success
        ? record.data
        : results?.find(user => user.client?.id?.toString() === id);
});

const showNoMatchMessage = computed(() => {
    return searchQuery.value.length >= 2 &&
        !isSearching.value &&
        !searchError.value &&
        !hasResults.value &&
        !isExistingClient.value;
});

watch(email, () => {
    clientEmail.value = email.value;
}, { immediate: true });
</script>


<template>
    <div>
        <UiFormField :label-capitalize="false" :name="props.name || ''" :label="props.label || ''" :model-value="email">
            <template #default="{ id, invalid, error, disabled: fieldDisabled, onValidate }">
                <div class="flex flex-col gap-1">
                    <UiAsyncCombobox v-model="searchQuery" v-model:selected-id="clientId" :id="id"
                        :disabled="props.disabled || fieldDisabled" :name="id" :invalid="invalid" v-model:selected-items="selectedItems"
                        :showSearchIcon="false" :data="searchResults" mode="preserve" hide-combobox-when-empty
                        :is-fetching="isSearching" placeholder="Type your client's name or email"
                        @blur="onValidate" show-selected-image />

                    <UiText v-if="error" as="p" variant="p-small" class="text-se-danger mt-2">
                        {{ error }}
                    </UiText>

                    <UiText v-else-if="searchError" as="p" variant="p-small" class="text-se-danger" text="Client search is unavailable. Please try again." />

                    <UiText v-else-if="showNoMatchMessage" as="p" variant="p-small"
                        class="text-se-gray-600 mt-2 bg-amber-100 p-4 rounded-md" text="No matching past client found. To invite an external client, enter their email and fill in their details below." />
                </div>
            </template>
        </UiFormField>
    </div>
</template>
